import React from 'react';
import ReactDOM from 'react-dom/client';
import { appsManager, common } from '@kurtosys/ksys-app-template';
import { IFeature } from '@kurtosys/types/app/IFeature.js';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { configure } from 'mobx';

import { getApplicationCode, getStoreKey } from './appStartUtils/app.js';
import { EVENTS } from './appStartUtils/events.js';
import { initializeGlobalDisclaimers } from './appStartUtils/globalDisclaimers.js';
import { initializeGlobalEvents } from './appStartUtils/globalEvents.js';
import { RE_INITIALIZE_ON_EMBED_ADD } from './constants/index.js';

// Isolates v4 Apps Mobx state so that they can be embedded with v2 Apps
configure({ isolateGlobalState: true });

// This is technically an import promise for the App Component. No official support for typing the imports
type TAppFn = () => Promise<any>;
// Difficult to get typings at this level for the injected store. Setting to any.
type TStoreContext = any;

type TGetStoreContextForElementFn = (
	element: HTMLElement,
	url: string,
	manifest: IManifest,
	storeKey?: string,
	reInitializeStore?: boolean,
) => TStoreContext;

interface IRenderOptions {
	event: Event | HTMLElement;
	forceRender?: boolean;
	reInitializeStore?: boolean;
}

if (typeof Node === 'function' && Node.prototype) {
	const originalRemoveChild = Node.prototype.removeChild;
	Node.prototype.removeChild = function (child) {
		if (child.parentNode !== this) {
			if (console) {
				console.warn('Cannot remove a child from a different parent', child, this);
			}
			// Returning null here, otherwise we end up with duplicate elements on the page
			return null;
		}
		// eslint-disable-next-line prefer-rest-params
		return originalRemoveChild.apply(this, arguments);
	};

	const originalInsertBefore = Node.prototype.insertBefore;
	Node.prototype.insertBefore = function (newNode, referenceNode) {
		if (referenceNode && referenceNode.parentNode !== this) {
			if (console) {
				console.warn('Cannot insert before a reference node from a different parent', referenceNode, this);
			}
			return newNode;
		}
		// eslint-disable-next-line prefer-rest-params
		return originalInsertBefore.apply(this, arguments);
	};
}

export class Start {
	manifest: IManifest;
	URL: string;
	hasManager: boolean;
	appImportFn: TAppFn;
	feature: IFeature;
	getStoreContextForElement: TGetStoreContextForElementFn;
	intersectionObserverHelper: common.helpers.IntersectionObserverHelper | undefined;
	constructor(
		feature: IFeature,
		manifest: IManifest,
		appImportFn: TAppFn,
		getStoreContextForElement: TGetStoreContextForElementFn,
	) {
		this.feature = feature;
		this.appImportFn = appImportFn;
		this.getStoreContextForElement = getStoreContextForElement;
		this.manifest = manifest;
		this.URL = window.location.href;
		const appManager = document.querySelectorAll('[data-ksys-app-template-id="ksys-app-manager"]');
		this.hasManager = !this.manifest.bypassAppManager && appManager.length > 0;
		if (this.hasManager) {
			this.addManagerEventListeners();
		}
		if (this.feature.useIntersectionObserver) {
			const options = {
				callback: this.renderElementFromObserver,
			};
			this.intersectionObserverHelper = new common.helpers.IntersectionObserverHelper(options);
		}
	}

	readyForRender = (element: HTMLElement) => {
		let managerReady = true;
		if (this.hasManager) {
			const dataset = element?.dataset;
			const managerInitialized = dataset?.managerInitialized === 'true';
			managerReady = managerInitialized;
		}
		return (
			managerReady &&
			!(element.dataset && element.dataset.dynamicLoad === 'true' && element.dataset.embedAdded !== 'true')
		);
	};

	renderAppToTargets = async (elements?: HTMLElement[], reInitializeStore = false) => {
		const applicationCode = getApplicationCode(this.manifest);
		if (!elements) {
			elements = appsManager.utils.getElementsForManifest(this.manifest);
		}

		elements = elements.filter(this.readyForRender);

		const elementsForApplication = elements.filter((element) => {
			const dataset = common.commonUtils.getOrchestratedDataset(element);
			const { applicationCode: elementApplicationCode } = dataset;
			return (
				!applicationCode ||
				!elementApplicationCode ||
				applicationCode === elementApplicationCode ||
				// Table app returns "33965ca4-dae0-4e9d-ba7b-d838e8362661/services/applicationManager/apps/9163092f-df17-4e59-ba29-e693d4037d92"
				// TODO: Track down why this value is returned by getApplicationCode(this.manifest) for table app.
				// This is a temp fix
				// So we check if the value includes the elementApplicationCode
				applicationCode.split('/').includes(elementApplicationCode as string)
			);
		});

		if (elementsForApplication && elementsForApplication.length > 0) {
			for (const element of elementsForApplication) {
				// Get the first child of the embed to use as the loading icon
				if (!element.dataset || element.dataset.initialized !== 'true') {
					const storeKey = getStoreKey(element, this.URL, this.manifest);
					const store = await this.getStoreContextForElement(
						element,
						this.URL,
						this.manifest,
						storeKey,
						reInitializeStore,
					);
					element.dataset.initialized = 'true';
					(element as any)['__ksysStoreKey__'] = storeKey;
					this.renderElement(element);
				}
			}
		}
	};

	/**
	 * Apps must always render when the isSnapshot=true query value is set
	 *
	 * If the useIntersectionObserver feature is turned on, the app can only render when the observed attribute is set
	 * by the intersection observer helper
	 */
	canRender = (element: HTMLElement) => {
		const urlParams = new URLSearchParams(window.location.search);
		const isSnapshot = urlParams.get('isSnapshot');
		const shouldRender = element.dataset && element.dataset.observed === 'true';
		const isObserved = !this.feature.useIntersectionObserver || shouldRender;
		return isSnapshot || isObserved;
	};

	renderElement = async (element: HTMLElement) => {
		// Lazy load the Provider and App components
		const { Provider } = await import('mobx-react');
		const { App } = await this.appImportFn();
		const embedLoadingElement = element.firstElementChild;
		const storeKey = (element as any)['__ksysStoreKey__'];
		if (storeKey && this.canRender(element)) {
			const storeContext = (window as any)[storeKey];
			const providerProps = (storeContext && storeContext.providerProps) || {};
			// This is the react 18 way of mounting, commenting out for now, there are issues
			// const ReactDOM = await import('react-dom/client');
			const rootReactNode = (ReactDOM as any).createRoot(element);
			rootReactNode.render(
				<Provider {...providerProps}>
					<App embedLoadingElement={embedLoadingElement} />
				</Provider>,
			);
			const appStore = storeContext.get('appStore');
			if (appStore) {
				appStore.didRender();
			}
		}
	};

	renderElementFromObserver = async (element: HTMLElement) => {
		if (!this.readyForRender(element)) {
			return undefined;
		}

		this.renderElement(element);
	};

	addEventListeners = () => {
		this.removeEventListeners();
		document.addEventListener(EVENTS.KSYS_APP_EMBED_ADDED, this.embedAddedHandler);
		document.addEventListener(EVENTS.KSYS_APP_PREVIEW_UPDATED, this.previewUpdatedHandler);
	};

	removeEventListeners = () => {
		document.removeEventListener(EVENTS.KSYS_APP_EMBED_ADDED, this.embedAddedHandler);
		document.addEventListener(EVENTS.KSYS_APP_PREVIEW_UPDATED, this.previewUpdatedHandler);
	};

	addManagerEventListeners = () => {
		document.addEventListener(EVENTS.KSYS_APP_MANAGER_INITIALIZED, this.managerInitializedHandler);
	};

	removeManagerEventListeners = () => {
		document.removeEventListener(EVENTS.KSYS_APP_MANAGER_INITIALIZED, this.managerInitializedHandler);
	};

	handleAppRender = ({ event, forceRender = false, reInitializeStore = false }: IRenderOptions) => {
		const target = event instanceof Event ? event.target : event;
		if (target) {
			const dataset = common.commonUtils.getOrchestratedDataset(target as HTMLElement);
			if (dataset) {
				const { ksysAppTemplateId, initialized } = dataset;
				if ((initialized !== 'true' || forceRender) && ksysAppTemplateId === this.manifest.ksysAppTemplateId) {
					// Initialize intersection observer for ApplicationEmbeds or Application Manager embeds
					this.initializeIntersectionObserver([target as HTMLElement]);
					this.renderAppToTargets([target as any], reInitializeStore);
				}
			}
		}
	};

	managerInitializedHandler = (event: Event | HTMLElement) => {
		this.handleAppRender({ event });
	};

	embedAddedHandler = (event: Event) => {
		const { target } = event;
		if (target) {
			const { dataset } = target as HTMLElement;

			dataset.embedAdded = 'true';
			this.handleAppRender({ event, reInitializeStore: RE_INITIALIZE_ON_EMBED_ADD });
		}
	};

	previewUpdatedHandler = (event: Event) => {
		const target = event.target as HTMLElement;
		if (!target) return;

		const storeContext = this.getStoreContextForElement(target, this.URL, this.manifest);
		if (!storeContext) return;

		const appStore = storeContext.get('appStore');
		if (!appStore || !appStore.loadPreviewContext) return;

		appStore.loadPreviewContext();
	};

	initializeIntersectionObserver = (elements?: HTMLElement[]) => {
		if (this.feature.useIntersectionObserver) {
			const appElements = elements ?? appsManager.utils.getElementsForManifest(this.manifest);
			this.intersectionObserverHelper.registerElements(appElements);
		}
	};

	go = () => {
		this.initializeIntersectionObserver();
		this.renderAppToTargets();
		initializeGlobalEvents();
		initializeGlobalDisclaimers();
		this.addEventListeners();
	};
}
